import { MIN_CLEANING_OVERRIDE_REASON, type BedBoardCell } from '@his/shared';

export const BED_STATUS_CHOICES = ['vacant', 'cleaning', 'reserved', 'blocked'] as const;

export const BOARD_STATUSES = ['occupied', 'reserved', 'cleaning', 'blocked', 'vacant'] as const;
export type BoardStatus = (typeof BOARD_STATUSES)[number];
export type StatusFilter = 'all' | BoardStatus | 'discharge_today';
export type DischargeDue = 'overdue' | 'today' | 'tomorrow';
export type AgeUnit = 'days' | 'months' | 'years';

const DAY_MS = 86_400_000;

export function boardStatusOf(status: string): BoardStatus {
  return (BOARD_STATUSES as readonly string[]).includes(status) ? (status as BoardStatus) : 'blocked';
}

export type StatusCounts = Record<BoardStatus, number> & { total: number };

export function countStatuses(cells: readonly BedBoardCell[]): StatusCounts {
  const counts: StatusCounts = { total: cells.length, occupied: 0, reserved: 0, cleaning: 0, blocked: 0, vacant: 0 };
  for (const cell of cells) counts[boardStatusOf(cell.status)] += 1;
  return counts;
}

export function occupancyPercent(counts: StatusCounts): number {
  return counts.total ? Math.round((counts.occupied / counts.total) * 100) : 0;
}

export function calendarDay(value: string | Date, timeZone: string): string {
  const date = new Date(value);
  try {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
    const part = (type: string) => parts.find((entry) => entry.type === type)?.value ?? '';
    return `${part('year')}-${part('month')}-${part('day')}`;
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

function daysBetween(fromDay: string, toDay: string): number {
  return Math.round((Date.parse(`${toDay}T00:00:00Z`) - Date.parse(`${fromDay}T00:00:00Z`)) / DAY_MS);
}

export function stayDay(admittedAt: string, now: Date, timeZone: string): number {
  return Math.max(1, daysBetween(calendarDay(admittedAt, timeZone), calendarDay(now, timeZone)) + 1);
}

export function dischargeDue(expectedAt: string | null, now: Date, timeZone: string): DischargeDue | null {
  if (!expectedAt) return null;
  const ahead = daysBetween(calendarDay(now, timeZone), calendarDay(expectedAt, timeZone));
  if (ahead < 0) return 'overdue';
  if (ahead === 0) return 'today';
  if (ahead === 1) return 'tomorrow';
  return null;
}

export function patientAge(
  dob: string | null,
  approximateYears: number | null,
  now: Date,
): { value: number; unit: AgeUnit; approximate: boolean } | null {
  const born = dob ? Date.parse(`${dob}T00:00:00Z`) : Number.NaN;
  if (!Number.isNaN(born)) {
    const days = Math.floor((now.getTime() - born) / DAY_MS);
    if (days < 0) return null;
    if (days < 31) return { value: days, unit: 'days', approximate: false };
    const months = Math.floor(days / 30.4375);
    if (months < 24) return { value: months, unit: 'months', approximate: false };
    const birth = new Date(born);
    const beforeBirthday =
      now.getUTCMonth() < birth.getUTCMonth() || (now.getUTCMonth() === birth.getUTCMonth() && now.getUTCDate() < birth.getUTCDate());
    return { value: now.getUTCFullYear() - birth.getUTCFullYear() - (beforeBirthday ? 1 : 0), unit: 'years', approximate: false };
  }
  if (approximateYears !== null) return { value: approximateYears, unit: 'years', approximate: true };
  return null;
}

export interface BoardFilter {
  status: StatusFilter;
  bedClass: string;
  sex: string;
  search: string;
}

export const EMPTY_BOARD_FILTER: BoardFilter = { status: 'all', bedClass: '', sex: '', search: '' };

export function activeFilterCount(filter: BoardFilter): number {
  return [filter.status !== 'all', filter.bedClass !== '', filter.sex !== '', filter.search.trim() !== ''].filter(Boolean).length;
}

export function matchesBoardFilter(cell: BedBoardCell, filter: BoardFilter, now: Date, timeZone: string): boolean {
  if (filter.status === 'discharge_today') {
    if (dischargeDue(cell.expectedDischargeAt, now, timeZone) !== 'today') return false;
  } else if (filter.status !== 'all' && boardStatusOf(cell.status) !== filter.status) {
    return false;
  }
  if (filter.bedClass && cell.bedClass !== filter.bedClass) return false;
  if (filter.sex && cell.patientSex !== filter.sex) return false;
  const needle = filter.search.trim().toLowerCase();
  if (!needle) return true;
  return [cell.patientName, cell.mrn, cell.bedCode].some((value) => value?.toLowerCase().includes(needle));
}

export function countDischargesDue(cells: readonly BedBoardCell[], now: Date, timeZone: string): { today: number; tomorrow: number } {
  let today = 0;
  let tomorrow = 0;
  for (const cell of cells) {
    const due = dischargeDue(cell.expectedDischargeAt, now, timeZone);
    if (due === 'today') today += 1;
    if (due === 'tomorrow') tomorrow += 1;
  }
  return { today, tomorrow };
}

export interface RoomGroup {
  key: string;
  roomCode: string;
  roomName: string | null;
  cells: BedBoardCell[];
}

export interface WardGroup {
  wardId: string;
  wardName: string;
  rooms: RoomGroup[];
}

export function groupByWardAndRoom(cells: readonly BedBoardCell[]): WardGroup[] {
  const wards = new Map<string, WardGroup>();
  const rooms = new Map<string, RoomGroup>();
  for (const cell of cells) {
    let ward = wards.get(cell.wardId);
    if (!ward) {
      ward = { wardId: cell.wardId, wardName: cell.wardName, rooms: [] };
      wards.set(cell.wardId, ward);
    }
    const key = `${cell.wardId}:${cell.roomCode}`;
    let room = rooms.get(key);
    if (!room) {
      room = { key, roomCode: cell.roomCode, roomName: cell.roomName, cells: [] };
      rooms.set(key, room);
      ward.rooms.push(room);
    }
    room.cells.push(cell);
  }
  return [...wards.values()];
}

export function wardCounts(cells: readonly BedBoardCell[]): Map<string, StatusCounts> {
  const byWard = new Map<string, BedBoardCell[]>();
  for (const cell of cells) byWard.set(cell.wardId, [...(byWard.get(cell.wardId) ?? []), cell]);
  return new Map([...byWard.entries()].map(([wardId, wardCells]) => [wardId, countStatuses(wardCells)]));
}

export function bedSignature(cell: BedBoardCell): string {
  return `${cell.status}|${cell.admissionId ?? ''}`;
}

export function changedBeds(previous: ReadonlyMap<string, string> | null, cells: readonly BedBoardCell[]): string[] {
  if (!previous) return [];
  return cells.filter((cell) => previous.get(cell.bedId) !== bedSignature(cell)).map((cell) => cell.bedId);
}

export interface RingSegment {
  status: BoardStatus;
  count: number;
  length: number;
  offset: number;
}

export function ringSegments(counts: StatusCounts, circumference: number, gap: number): RingSegment[] {
  if (!counts.total) return [];
  const present = BOARD_STATUSES.filter((status) => counts[status] > 0);
  const spacing = present.length > 1 ? gap : 0;
  let start = 0;
  return present.map((status) => {
    const span = (counts[status] / counts.total) * circumference;
    const segment = { status, count: counts[status], length: Math.max(span - spacing, 0.5), offset: start };
    start += span;
    return segment;
  });
}

export function releasesBedBeforeCleaningIsDone(currentStatus: string, nextStatus: string): boolean {
  return currentStatus === 'cleaning' && nextStatus === 'vacant';
}

export function bedStatusChangeReady(input: { currentStatus: string; nextStatus: string; reason: string }): boolean {
  if (input.nextStatus === input.currentStatus) return false;
  if (!releasesBedBeforeCleaningIsDone(input.currentStatus, input.nextStatus)) return true;
  return input.reason.trim().length >= MIN_CLEANING_OVERRIDE_REASON;
}
